import React from 'react';
import { Trash2 } from 'lucide-react';
import QuickClientForm from './QuickClientForm';

const STATUS_LIST = ['brouillon', 'envoye', 'accepte', 'refuse', 'expire'];

export default function QuoteForm({
  form, setForm, editing, clients, vehicles, emptyItem,
  showQuickClient, setShowQuickClient, setClients,
  authHeaders, onSave, onClose
}) {
  const clientVehicles = form.client_id ? vehicles.filter(v => v.client_id === form.client_id) : [];

  const updateItem = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: field === 'description' || field === 'type' ? value : parseFloat(value) || 0 };
    setForm({ ...form, items });
  };

  const calcTotal = () => {
    const sub = form.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0), 0);
    const disc = sub * (form.discount || 0) / 100;
    const tva = form.items.reduce((s, i) => s + (i.quantity || 0) * (i.unit_price || 0) * (i.tva_rate || 20) / 100, 0) * (1 - (form.discount || 0) / 100);
    return { subtotal: sub.toFixed(2), tva: tva.toFixed(2), total: (sub - disc + tva).toFixed(2) };
  };

  const t = calcTotal();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl w-full max-w-3xl my-8">
        <div className="p-5 border-b flex justify-between items-center">
          <h3 className="font-semibold text-lg">{editing ? `Modifier ${editing.number}` : 'Nouveau devis'}</h3>
          <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} className="border rounded-lg px-3 py-1.5 text-sm">
            {STATUS_LIST.map(s => <option key={s} value={s} className="capitalize">{s}</option>)}
          </select>
        </div>
        <form onSubmit={onSave} className="p-5 space-y-4" data-testid="quote-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <div className="flex flex-wrap gap-2">
                <select required value={form.client_id} onChange={e => setForm({ ...form, client_id: e.target.value, vehicle_id: '' })} className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-0" data-testid="quote-client-select">
                  <option value="">Client *</option>
                  {clients.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.company_name}</option>)}
                </select>
                <button type="button" onClick={() => setShowQuickClient(!showQuickClient)} className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 shrink-0" data-testid="quote-new-client-btn" title="Créer un nouveau client">
                  + Client
                </button>
              </div>
            </div>
            <select value={form.vehicle_id || ''} onChange={e => setForm({ ...form, vehicle_id: e.target.value })} className="border rounded-lg px-3 py-2 text-sm">
              <option value="">Véhicule (optionnel)</option>
              {clientVehicles.map(v => <option key={v.id} value={v.id}>{v.brand} {v.model} - {v.plate}</option>)}
            </select>
          </div>

          {showQuickClient && (
            <QuickClientForm
              authHeaders={authHeaders}
              onCreated={(newClient) => {
                setClients(prev => [...prev, newClient]);
                setForm(prev => ({ ...prev, client_id: newClient.id, vehicle_id: '' }));
                setShowQuickClient(false);
              }}
              onCancel={() => setShowQuickClient(false)}
            />
          )}

          <div>
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-semibold">Lignes</h4>
              <button type="button" onClick={() => setForm({ ...form, items: [...form.items, { ...emptyItem }] })} className="text-xs text-[#DC2626] hover:underline">+ Ajouter une ligne</button>
            </div>
            {form.items.map((item, idx) => (
              <div key={`item-${idx}`} className="grid grid-cols-12 gap-2 mb-2 items-center">
                <select value={item.type} onChange={e => updateItem(idx, 'type', e.target.value)} className="col-span-2 border rounded px-2 py-1.5 text-xs">
                  <option value="service">M.O.</option><option value="piece">Pièce</option>
                </select>
                <input placeholder="Description" value={item.description} onChange={e => updateItem(idx, 'description', e.target.value)} className="col-span-4 border rounded px-2 py-1.5 text-xs" />
                <input type="number" step="0.01" placeholder="Qté" value={item.quantity} onChange={e => updateItem(idx, 'quantity', e.target.value)} className="col-span-1 border rounded px-2 py-1.5 text-xs text-center" />
                <input type="number" step="0.01" placeholder="Prix HT" value={item.unit_price} onChange={e => updateItem(idx, 'unit_price', e.target.value)} className="col-span-2 border rounded px-2 py-1.5 text-xs text-right" />
                <input type="number" placeholder="TVA%" value={item.tva_rate} onChange={e => updateItem(idx, 'tva_rate', e.target.value)} className="col-span-1 border rounded px-2 py-1.5 text-xs text-center" />
                <span className="col-span-1 text-xs text-right font-medium">{((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}€</span>
                <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, i) => i !== idx) })} className="col-span-1 text-red-400 hover:text-red-600 text-center"><Trash2 size={14} /></button>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600">Remise (%) :</label>
            <input type="number" min="0" max="100" value={form.discount} onChange={e => setForm({ ...form, discount: parseFloat(e.target.value) || 0 })} className="w-20 border rounded px-2 py-1.5 text-sm text-center" />
          </div>

          <div className="bg-gray-50 rounded-lg p-4 text-right space-y-1">
            <p className="text-sm">Total HT: <span className="font-semibold">{t.subtotal}€</span></p>
            {form.discount > 0 && <p className="text-sm text-red-600">Remise ({form.discount}%): -{(parseFloat(t.subtotal) * form.discount / 100).toFixed(2)}€</p>}
            <p className="text-sm">TVA: <span className="font-semibold">{t.tva}€</span></p>
            <p className="text-lg font-bold text-[#DC2626]">Total TTC: {t.total}€</p>
          </div>

          <textarea placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full border rounded-lg px-3 py-2 text-sm" />
          <input placeholder="Conditions" value={form.conditions} onChange={e => setForm({ ...form, conditions: e.target.value })} className="w-full border rounded-lg px-3 py-2 text-sm" />

          <div className="flex justify-end gap-3 pt-3">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
            <button type="submit" className="px-4 py-2 text-sm bg-[#DC2626] text-white rounded-lg hover:bg-red-700" data-testid="save-quote-btn">{editing ? 'Enregistrer' : 'Créer le devis'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
